<script setup>
import draggable from 'vuedraggable';
import { useQuestionsStore, useAnswersStore, useTestsStore } from '@stores';
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
import Editor from './Editor';
import UploadImage from './UploadImage.vue';
import AnswerButtons from './AnswerButtons.vue';

const { t } = useI18n({ useScope: 'global' });
const questionsStore = useQuestionsStore();
const answersStore = useAnswersStore();
const testsStore = useTestsStore();

const previousVal = ref({});
const dragOptions = computed(() => ({
    animation: 200,
    group: 'description',
    disabled: false,
    ghostClass: 'ghost',
}));

const updateOrderHandler = (event) => {
    answersStore.updateAnswerOrder(event.newIndex);
};

const setOriginalValue = (answer) => {
    previousVal.value[answer.language] = { ...answer };
};

const updateAnswerHandler = async (answer, value) => {
    if (value.language) {
        const prevValue = previousVal.value[value.language];
        if (prevValue.content === value.content && prevValue.key === value.key) return;
    }
    await answersStore.updateAnswer(answer, value);
};

const showLangTitle = computed(() => testsStore.main.selectedLanguages.length > 1);
</script>

<template>
    <div class="input-wrapper">
        <label for="answer-variant">{{ t('tests.answer') }}</label>
        <draggable :scroll-sensitivity="200" :force-fallback="true" v-model="questionsStore.question.answers" item-key="answer_id" v-bind="dragOptions" handle=".drag-handler" class="answer-variants" @end="updateOrderHandler" v-if="questionsStore.question.type != 3 && questionsStore.question.type != 4">
            <template #item="{ element }">
                <div class="answer-variant">
                    <UploadImage v-if="questionsStore.question.type == 2" v-model="element.answer[0].image" :id="element.answer_id" @update:modelValue="updateAnswerHandler(element, { image: element.answer[0].image })" />

                    <div class="answer-input-wrapper" v-for="answer in element.answer" :key="answer">
                        <span class="answer-language" v-if="showLangTitle">{{ answer.language.toUpperCase() }}</span>
                        <Editor v-model="answer.content" class="answer-input" @blur="updateAnswerHandler(element, answer)" @focus="setOriginalValue(answer)" />
                    </div>
                    <AnswerButtons :element="element" />
                </div>
            </template>
        </draggable>

        <div class="answer-inputs" v-if="questionsStore.question.type == 3">
            <InputGroup v-for="answer in questionsStore.question.answers[0].answer" :key="answer">
                <InputGroupAddon v-if="showLangTitle">{{ answer.language.toUpperCase() }}</InputGroupAddon>
                <InputText v-model="answer.content" placeholder="Type something..." @blur="updateAnswerHandler(questionsStore.question.answers[0], answer)" @focus="setOriginalValue(answer)" />
            </InputGroup>
        </div>

        <draggable :scroll-sensitivity="200" :force-fallback="true" v-model="questionsStore.question.answers" item-key="answer_id" v-bind="dragOptions" handle=".drag-handler" class="answer-variants" @end="updateOrderHandler" v-if="questionsStore.question.type == 4">
            <template #item="{ element }">
                <div class="answer-variant">
                    <div class="flex flex-col gap-4 w-full">
                        <div v-for="answer in element.answer" :key="answer" class="flex items-start gap-16 w-full">
                            <div class="answer-input-wrapper">
                                <span class="answer-language" v-if="showLangTitle">{{ answer.language.toUpperCase() }}</span>
                                <Editor v-model="answer.key" :imagable="true" class="answer-input" @blur="updateAnswerHandler(element, answer)" @focus="setOriginalValue(answer)" />
                            </div>
                            <div class="answer-input-wrapper">
                                <span class="answer-language" v-if="showLangTitle">{{ answer.language.toUpperCase() }}</span>
                                <Editor v-model="answer.content" :imagable="true" class="answer-input" @blur="updateAnswerHandler(element, answer)" @focus="setOriginalValue(answer)" />
                            </div>
                        </div>
                    </div>
                    <AnswerButtons :element="element" />
                </div>
            </template>
        </draggable>
    </div>
</template>

<style scoped>
.input-wrapper {
    @apply flex flex-col gap-2 w-full;
}
.answer-variants {
    @apply flex flex-col gap-6;
}
.answer-variant {
    @apply flex items-start gap-4 bg-gray-100 rounded-xl p-2;
}
.answer-input-wrapper {
    @apply flex items-stretch border border-surface-300 rounded-md w-full;
}
.answer-input {
    @apply p-2 w-full;
}
.answer-language {
    @apply p-2 border-r border-surface-300;
}
.answer-inputs {
    @apply flex flex-col gap-4;
}
</style>
